import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import AppLayout from '../components/AppLayout';
import { AlertTriangle } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ComparisonData {
  report1: any;
  report2: any;
  differences: Array<{
    field: string;
    report1: number;
    report2: number;
    difference: number;
  }>;
  summary: {
    total_differences: number;
    final_claim_difference: number;
  };
}

export default function CompareReports() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReport1, setSelectedReport1] = useState<string>(searchParams.get('r1') || '');
  const [selectedReport2, setSelectedReport2] = useState<string>(searchParams.get('r2') || '');
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    if (selectedReport1 && selectedReport2) {
      compareReports();
    }
  }, [selectedReport1, selectedReport2]);

  const loadReports = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${BACKEND_URL}/api/v1/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  };

  const compareReports = async () => {
    if (!selectedReport1 || !selectedReport2) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/reports/compare/${selectedReport1}/${selectedReport2}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparison(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to compare reports');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <AppLayout>
      <div className="p-6" style={{ background: 'var(--color-bg-base)', minHeight: '100%' }}>
        <div className="mb-6">
          <h1 className="cs-display">Compare Reports</h1>
          <p className="cs-meta" style={{ marginTop: 4 }}>Side-by-side comparison of claim calculations</p>
        </div>

        {/* Report Selection */}
        <div
          className="mb-6"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
        >
          <h2 className="cs-h2" style={{ marginBottom: 16 }}>Select Reports to Compare</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="cs-label" style={{ display: 'block', marginBottom: 8 }}>Report 1</label>
              <select
                value={selectedReport1}
                onChange={(e) => setSelectedReport1(e.target.value)}
                className="cs-body w-full"
                style={{ padding: '8px 12px', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-text-secondary)' }}
              >
                <option value="">Select a report...</option>
                {reports.map((report) => {
                  const clientName = report.data?.client_name || report.client_name || 'Unknown Client';
                  const claimAmount = (report.net_supported_claim ?? report.result?.final_claim_amount)
                    ? formatCurrency(report.net_supported_claim ?? report.result.final_claim_amount)
                    : report.status === 'draft' ? 'Draft' : 'Pending';
                  const shortId = report.id.substring(0, 8);
                  const date = new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const fromBulk = report.data?.fromBulkUpload ? '📤' : '';
                  
                  return (
                    <option key={report.id} value={report.id}>
                      {fromBulk} {clientName} | {claimAmount} | {date} | ID: {shortId}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="cs-label" style={{ display: 'block', marginBottom: 8 }}>Report 2</label>
              <select
                value={selectedReport2}
                onChange={(e) => setSelectedReport2(e.target.value)}
                className="cs-body w-full"
                style={{ padding: '8px 12px', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-text-secondary)' }}
              >
                <option value="">Select a report...</option>
                {reports.map((report) => {
                  const clientName = report.data?.client_name || report.client_name || 'Unknown Client';
                  const claimAmount = (report.net_supported_claim ?? report.result?.final_claim_amount)
                    ? formatCurrency(report.net_supported_claim ?? report.result.final_claim_amount)
                    : report.status === 'draft' ? 'Draft' : 'Pending';
                  const shortId = report.id.substring(0, 8);
                  const date = new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const fromBulk = report.data?.fromBulkUpload ? '📤' : '';
                  
                  return (
                    <option key={report.id} value={report.id}>
                      {fromBulk} {clientName} | {claimAmount} | {date} | ID: {shortId}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div
              className="animate-spin rounded-full h-12 w-12 mx-auto mb-4"
              style={{ borderBottom: '4px solid var(--color-brand)' }}
            ></div>
            <p className="cs-meta">Comparing reports...</p>
          </div>
        )}

        {error && (
          <div
            className="cs-body mb-6"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)', padding: 16, borderRadius: 8 }}
          >
            {error}
          </div>
        )}

        {comparison && !loading && (
          <>
            {/* Summary */}
            <div
              className="mb-6"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
            >
              <h2 className="cs-h2" style={{ marginBottom: 16 }}>Summary</h2>
              <div className="grid grid-cols-3 gap-4">
                <div style={{ background: 'var(--color-bg-elevated)', padding: 16, borderRadius: 12 }}>
                  <p className="cs-label">Total Differences</p>
                  <p className="cs-figure-lg" style={{ marginTop: 6 }}>{comparison.summary.total_differences}</p>
                </div>
                <div
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    background: comparison.summary.final_claim_difference >= 0 ? 'transparent' : 'var(--color-bg-elevated)',
                  }}
                >
                  <p
                    className="cs-label"
                    style={{ color: comparison.summary.final_claim_difference >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}
                  >
                    Final Claim Difference
                  </p>
                  <p
                    className="cs-figure-lg"
                    style={{ marginTop: 6, color: comparison.summary.final_claim_difference >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}
                  >
                    <FinancialValue value={comparison.summary.final_claim_difference} />
                  </p>
                </div>
                <div style={{ background: 'var(--color-bg-elevated)', padding: 16, borderRadius: 12 }}>
                  <p className="cs-label">Bulk Upload</p>
                  <p className="cs-body" style={{ marginTop: 6 }}>
                    R1: {comparison.report1.from_bulk_upload ? '✅ Yes' : '❌ No'} |
                    R2: {comparison.report2.from_bulk_upload ? '✅ Yes' : '❌ No'}
                  </p>
                </div>
              </div>
            </div>

            {/* Side by Side Comparison */}
            <div
              className="mb-6"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
            >
              <h2 className="cs-h2" style={{ marginBottom: 16 }}>Side-by-Side Comparison</h2>

              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: 'var(--color-bg-elevated)' }}>
                    <th className="cs-label text-left" style={{ padding: 12, borderBottom: '1px solid var(--color-border)' }}>Field</th>
                    <th className="cs-label text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border)' }}>Report 1</th>
                    <th className="cs-label text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border)' }}>Report 2</th>
                    <th className="cs-label text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border)' }}>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Report Info */}
                  <tr style={{ background: 'var(--color-bg-base)' }}>
                    <td colSpan={4} className="cs-eyebrow" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Report Info</td>
                  </tr>
                  <tr>
                    <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Title</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>{comparison.report1.title || 'N/A'}</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>{comparison.report2.title || 'N/A'}</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: 'var(--color-text-ghost)' }}>-</td>
                  </tr>
                  <tr>
                    <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Created</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>{formatDate(comparison.report1.created_at)}</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>{formatDate(comparison.report2.created_at)}</td>
                    <td className="cs-body text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: 'var(--color-text-ghost)' }}>-</td>
                  </tr>

                  {/* Revenue by Year */}
                  <tr style={{ background: 'var(--color-bg-base)' }}>
                    <td colSpan={4} className="cs-eyebrow" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Revenue by Year</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const r1 = comparison.report1.inputs.revenue_by_year[year] || 0;
                    const r2 = comparison.report2.inputs.revenue_by_year[year] || 0;
                    const diff = r2 - r1;
                    return (
                      <tr key={`rev-${year}`} style={diff !== 0 ? { background: 'var(--color-needs-human-tint)' } : undefined}>
                        <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Revenue {year}</td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={r1} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={r2} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: diff !== 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-ghost)', fontWeight: diff !== 0 ? 600 : 400 }}>
                          {diff !== 0 ? <FinancialValue value={diff} /> : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Payroll by Year */}
                  <tr style={{ background: 'var(--color-bg-base)' }}>
                    <td colSpan={4} className="cs-eyebrow" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Payroll by Year (from financial_data)</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const p1 = comparison.report1.inputs.payroll_by_year[year] || 0;
                    const p2 = comparison.report2.inputs.payroll_by_year[year] || 0;
                    const diff = p2 - p1;
                    return (
                      <tr key={`pay-${year}`} style={diff !== 0 ? { background: 'var(--color-needs-human-tint)' } : undefined}>
                        <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Payroll {year}</td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={p1} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={p2} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: diff !== 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-ghost)', fontWeight: diff !== 0 ? 600 : 400 }}>
                          {diff !== 0 ? <FinancialValue value={diff} /> : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Step 4 Payroll Inputs */}
                  <tr style={{ background: 'var(--color-bg-base)' }}>
                    <td colSpan={4} className="cs-eyebrow" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Step 4 Payroll Inputs</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const s1 = comparison.report1.inputs.step4_payroll[year] || 0;
                    const s2 = comparison.report2.inputs.step4_payroll[year] || 0;
                    const diff = Number(s2) - Number(s1);
                    return (
                      <tr key={`step4-${year}`} style={diff !== 0 ? { background: 'var(--color-needs-human-tint)' } : undefined}>
                        <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Step 4 Payroll {year}</td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={Number(s1)} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={Number(s2)} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: diff !== 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-ghost)', fontWeight: diff !== 0 ? 600 : 400 }}>
                          {diff !== 0 ? <FinancialValue value={diff} /> : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Calculation Results */}
                  <tr style={{ background: 'var(--color-bg-base)' }}>
                    <td colSpan={4} className="cs-eyebrow" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>Calculation Results</td>
                  </tr>
                  {[
                    { key: 'benchmark_variable_profit', label: 'Benchmark Variable Profit' },
                    { key: 'coverage_variable_profit', label: 'Coverage Variable Profit' },
                    { key: 'variable_profit_loss', label: 'Variable Profit Loss' },
                    { key: 'fixed_payroll_baseline', label: 'Fixed Payroll Baseline' },
                    { key: 'rtp_adjustment', label: 'RTP Adjustment' },
                    { key: 'final_claim', label: 'Final Claim' },
                  ].map(({ key, label }) => {
                    const r1 = comparison.report1.results[key] || 0;
                    const r2 = comparison.report2.results[key] || 0;
                    const diff = r2 - r1;
                    return (
                      <tr key={key} style={diff !== 0 ? { background: 'var(--color-needs-human-tint)' } : undefined}>
                        <td className="cs-body" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}>{label}</td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={r1} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)' }}><FinancialValue value={r2} /></td>
                        <td className="cs-figure text-right" style={{ padding: 12, borderBottom: '1px solid var(--color-border-faint)', color: diff !== 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-ghost)', fontWeight: diff !== 0 ? 600 : 400 }}>
                          {diff !== 0 ? <FinancialValue value={diff} /> : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Differences Highlight */}
            {comparison.differences.length > 0 && (
              <div style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: 24 }}>
                <h2 className="cs-h2 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)', marginBottom: 16 }}><AlertTriangle className="w-5 h-5" aria-hidden="true" /> Key Differences Found</h2>
                <ul className="space-y-2">
                  {comparison.differences.map((diff, idx) => (
                    <li
                      key={idx}
                      className="flex justify-between items-center"
                      style={{ padding: 8, background: 'var(--color-bg-surface)', borderRadius: 8, border: '1px solid var(--color-border)' }}
                    >
                      <span className="cs-body" style={{ fontWeight: 600 }}>{diff.field}</span>
                      <span className="cs-figure">
                        R1: <FinancialValue value={diff.report1} /> → R2: <FinancialValue value={diff.report2} />
                        <span
                          className="cs-figure"
                          style={{ marginLeft: 8, fontWeight: 600, color: diff.difference >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}
                        >
                          {diff.difference >= 0 ? `(+` : `(`}
                          <FinancialValue value={diff.difference} />
                          {`)`}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
